import React, { useState } from 'react';
import { View, StyleSheet, ScrollView, Linking, Alert } from 'react-native';
import {
  Text,
  List,
  Card,
  useTheme,
  Button,
  Divider,
  Searchbar,
} from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import {
  HelpCircle,
  MessageCircle,
  Mail,
  Phone,
  Globe,
  BookOpen,
  Video,
  Coffee,
  Users,
  Settings,
  Heart,
  TrendingUp,
  Shield,
  FileText,
  ExternalLink,
  ChevronRight,
} from 'lucide-react-native';
import Toast from 'react-native-toast-message';

interface FAQItem {
  title: string;
  content: string;
  category: string;
}

interface SupportOption {
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  action: () => void;
  color?: string;
}

export function HelpSupportScreen() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedAccordion, setExpandedAccordion] = useState<string | null>(
    null
  );

  const faqData: FAQItem[] = [
    {
      title: 'How do I create my first recipe?',
      content:
        "To create your first recipe, tap the '+' button on the Recipes tab or use the FAB button on My Recipes. Fill in the recipe details including brewing method, ingredients, steps, and brewing parameters. You can save as a draft or publish immediately.",
      category: 'Recipes',
    },
    {
      title: 'How do I change my profile picture?',
      content:
        'Go to Profile > Edit Profile and tap the camera icon on your avatar. You can choose to take a new photo or select from your gallery. The image will be automatically uploaded and updated.',
      category: 'Profile',
    },
    {
      title: 'How do I follow other creators?',
      content:
        "Visit any creator's profile and tap the 'Follow' button. You'll receive notifications about their new recipes and can see their content in your feed. You can manage your following list in Profile > Following.",
      category: 'Social',
    },
    {
      title: 'How do I save recipes to favorites?',
      content:
        "While viewing any recipe, tap the heart icon to save it to your favorites. You can access all your saved recipes in the Favorites tab. You can also organize them by categories like 'Bookmarked' or 'Liked'.",
      category: 'Recipes',
    },
    {
      title: 'How do I adjust notification settings?',
      content:
        'Go to Profile > Notifications or Profile > Settings to customize your notification preferences. You can toggle push notifications, email notifications, and specific types like recipe likes, new followers, and brewing reminders.',
      category: 'Settings',
    },
    {
      title: 'How do I use the AI Experience features?',
      content:
        'The AI Experience Hub offers personalized coffee journey recommendations, brewing tips, and recipe suggestions. Complete the assessment to get started, and the AI will learn your preferences over time to provide better recommendations.',
      category: 'AI Features',
    },
    {
      title: 'How do I report inappropriate content?',
      content:
        "If you encounter inappropriate content, tap the three dots menu on any recipe or profile and select 'Report'. Choose the reason and provide details. Our moderation team will review the report within 24 hours.",
      category: 'Safety',
    },
    {
      title: 'How do I export my data?',
      content:
        "Go to Profile > Privacy & Security > Export My Data. This will generate a comprehensive export of all your data including recipes, preferences, and activity history. You'll receive an email when it's ready for download.",
      category: 'Privacy',
    },
    {
      title: 'How do I change my password?',
      content:
        'Go to Profile > Privacy & Security > Change Password. Enter your current password and choose a new one. Make sure your new password is at least 8 characters long and includes a mix of letters, numbers, and symbols.',
      category: 'Security',
    },
    {
      title: 'How do I delete my account?',
      content:
        'Go to Profile > Privacy & Security > Delete Account. Enter your password to confirm. This action cannot be undone and will permanently remove all your data, recipes, and preferences.',
      category: 'Account',
    },
  ];

  const supportOptions: SupportOption[] = [
    {
      title: 'Contact Support',
      description: 'Get help from our support team',
      icon: MessageCircle,
      action: () => openSupportChat(),
      color: theme.colors.primary,
    },
    {
      title: 'Email Support',
      description: 'Send us an email',
      icon: Mail,
      action: () => openEmailSupport(),
      color: theme.colors.secondary,
    },
    {
      title: 'Phone Support',
      description: 'Call us directly',
      icon: Phone,
      action: () => openPhoneSupport(),
      color: theme.colors.tertiary,
    },
    {
      title: 'Live Chat',
      description: 'Chat with support in real-time',
      icon: MessageCircle,
      action: () => openLiveChat(),
      color: theme.colors.primary,
    },
  ];

  const quickActions = [
    {
      title: 'Create Recipe',
      icon: Coffee,
      action: () => navigation.navigate('createRecipe' as never),
    },
    {
      title: 'My Recipes',
      icon: BookOpen,
      action: () => navigation.navigate('myRecipes' as never),
    },
    {
      title: 'Favorites',
      icon: Heart,
      action: () => navigation.navigate('favorites' as never),
    },
    {
      title: 'Settings',
      icon: Settings,
      action: () => navigation.navigate('settings' as never),
    },
    {
      title: 'Privacy',
      icon: Shield,
      action: () => navigation.navigate('privacySecurity' as never),
    },
    {
      title: 'AI Experience',
      icon: TrendingUp,
      action: () => navigation.navigate('aiExperience' as never),
    },
  ];

  const openSupportChat = () => {
    Toast.show({
      type: 'info',
      text1: 'Support Chat',
      text2: 'Opening support chat...',
    });
  };

  const openEmailSupport = () => {
    const subject = encodeURIComponent('CoffeePlexity Support Request');
    const body = encodeURIComponent(
      'Hello CoffeePlexity Support Team,\n\nI need help with:\n\n[Please describe your issue here]\n\nThank you!'
    );
    const mailto = `mailto:support@coffeeplexity.com?subject=${subject}&body=${body}`;

    Linking.canOpenURL(mailto).then(supported => {
      if (supported) {
        Linking.openURL(mailto);
      } else {
        Toast.show({
          type: 'error',
          text1: 'Email not available',
          text2: 'Please email support@coffeeplexity.com directly',
        });
      }
    });
  };

  const openPhoneSupport = () => {
    Alert.alert(
      'Phone Support',
      'Call us at +1 (555) 123-4567\n\nAvailable Monday-Friday, 9 AM - 6 PM EST',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Call Now',
          onPress: () => {
            Linking.openURL('tel:+15551234567');
          },
        },
      ]
    );
  };

  const openLiveChat = () => {
    Toast.show({
      type: 'info',
      text1: 'Live Chat',
      text2: 'Connecting you to a support agent...',
    });
  };

  const openHelpCenter = () => {
    Linking.openURL('https://help.coffeeplexity.com');
  };

  const openVideoTutorials = () => {
    Linking.openURL('https://coffeeplexity.com/tutorials');
  };

  const openCommunityForum = () => {
    Linking.openURL('https://community.coffeeplexity.com');
  };

  const getFilteredFAQ = () => {
    if (!searchQuery.trim()) return faqData;

    const query = searchQuery.toLowerCase();
    return faqData.filter(
      item =>
        item.title.toLowerCase().includes(query) ||
        item.content.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query)
    );
  };

  const renderFAQItem = (item: FAQItem, index: number) => (
    <List.Accordion
      key={index}
      title={item.title}
      description={item.category}
      left={props => <HelpCircle {...props} size={24} />}
      expanded={expandedAccordion === `faq-${index}`}
      onPress={() =>
        setExpandedAccordion(
          expandedAccordion === `faq-${index}` ? null : `faq-${index}`
        )
      }
    >
      <View style={styles.faqContent}>
        <Text variant='bodyMedium'>{item.content}</Text>
      </View>
    </List.Accordion>
  );

  const renderQuickAction = (action: any, index: number) => (
    <Button
      key={index}
      mode='outlined'
      onPress={action.action}
      icon={action.icon}
      style={styles.quickActionButton}
      contentStyle={styles.quickActionContent}
    >
      {action.title}
    </Button>
  );

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      {/* Header */}
      <View style={styles.header}>
        <Text variant='headlineSmall' style={styles.headerTitle}>
          Help & Support
        </Text>
        <Text variant='bodyMedium' style={styles.headerSubtitle}>
          Get help with CoffeePlexity
        </Text>
      </View>

      {/* Search */}
      <Searchbar
        placeholder='Search help topics...'
        onChangeText={setSearchQuery}
        value={searchQuery}
        style={styles.searchBar}
      />

      {/* Quick Actions */}
      <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
        <Card.Content>
          <Text variant='titleMedium' style={styles.sectionTitle}>
            Quick Actions
          </Text>
          <View style={styles.quickActionsGrid}>
            {quickActions.map(renderQuickAction)}
          </View>
        </Card.Content>
      </Card>

      {/* Support Options */}
      <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
        <Card.Content>
          <Text variant='titleMedium' style={styles.sectionTitle}>
            Get Help
          </Text>
          {supportOptions.map((option, index) => (
            <React.Fragment key={index}>
              <List.Item
                title={option.title}
                description={option.description}
                left={() => (
                  <option.icon
                    size={24}
                    color={option.color || theme.colors.outline}
                  />
                )}
                right={() => (
                  <ChevronRight size={20} color={theme.colors.outline} />
                )}
                onPress={option.action}
                style={styles.supportOption}
              />
              {index < supportOptions.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Card.Content>
      </Card>

      {/* External Resources */}
      <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
        <Card.Content>
          <Text variant='titleMedium' style={styles.sectionTitle}>
            Resources
          </Text>

          <List.Item
            title='Help Center'
            description='Browse our comprehensive help articles'
            left={() => <FileText size={24} color={theme.colors.outline} />}
            right={() => (
              <ExternalLink size={20} color={theme.colors.outline} />
            )}
            onPress={openHelpCenter}
          />
          <Divider />
          <List.Item
            title='Video Tutorials'
            description='Learn with step-by-step video guides'
            left={() => <Video size={24} color={theme.colors.outline} />}
            right={() => (
              <ExternalLink size={20} color={theme.colors.outline} />
            )}
            onPress={openVideoTutorials}
          />
          <Divider />
          <List.Item
            title='Community Forum'
            description='Connect with other coffee enthusiasts'
            left={() => <Users size={24} color={theme.colors.outline} />}
            right={() => (
              <ExternalLink size={20} color={theme.colors.outline} />
            )}
            onPress={openCommunityForum}
          />
        </Card.Content>
      </Card>

      {/* FAQ Section */}
      <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
        <Card.Content>
          <Text variant='titleMedium' style={styles.sectionTitle}>
            Frequently Asked Questions
          </Text>
          {getFilteredFAQ().map((item, index) => renderFAQItem(item, index))}
        </Card.Content>
      </Card>

      {/* Contact Information */}
      <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
        <Card.Content>
          <Text variant='titleMedium' style={styles.sectionTitle}>
            Contact Information
          </Text>

          <View style={styles.contactInfo}>
            <View style={styles.contactItem}>
              <Mail size={20} color={theme.colors.outline} />
              <Text variant='bodyMedium'>support@coffeeplexity.com</Text>
            </View>

            <View style={styles.contactItem}>
              <Phone size={20} color={theme.colors.outline} />
              <Text variant='bodyMedium'>+1 (555) 123-4567</Text>
            </View>

            <View style={styles.contactItem}>
              <Globe size={20} color={theme.colors.outline} />
              <Text variant='bodyMedium'>coffeeplexity.com/support</Text>
            </View>
          </View>

          <Text variant='bodySmall' style={styles.contactNote}>
            Our support team is available Monday-Friday, 9 AM - 6 PM EST
          </Text>
        </Card.Content>
      </Card>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
  },
  header: {
    alignItems: 'center',
    marginBottom: 24,
  },
  headerTitle: {
    fontWeight: '600',
    marginBottom: 8,
  },
  headerSubtitle: {
    opacity: 0.7,
    textAlign: 'center',
  },
  searchBar: {
    marginBottom: 16,
    elevation: 0,
  },
  card: {
    marginBottom: 16,
    elevation: 2,
  },
  sectionTitle: {
    fontWeight: '600',
    marginBottom: 16,
  },
  quickActionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  quickActionButton: {
    flex: 1,
    minWidth: '45%',
  },
  quickActionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  supportOption: {
    paddingVertical: 8,
  },
  faqContent: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  contactInfo: {
    gap: 12,
    marginBottom: 16,
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  contactNote: {
    opacity: 0.7,
    fontStyle: 'italic',
    textAlign: 'center',
  },
});
